import { useCallback, useEffect, useRef, useState } from "react";
import type { ChatMessage, WorkspaceState } from "../types";
import { cacheGeneratedResources, isTemporaryResourceUrl, mergeCachedResources } from "./resource-cache";

export function useResourceCache(workspace: WorkspaceState, hydrated: boolean, updateMessage: (conversationId: string, messageId: string, update: (message: ChatMessage) => ChatMessage) => void) {
  const attempted = useRef(new Set<string>());
  const queue = useRef<Array<{ key: string; conversationId: string; message: ChatMessage }>>([]);
  const processing = useRef(false);
  const [failed, setFailed] = useState(0);
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!hydrated) return;
    for (const conversation of workspace.conversations) for (const message of conversation.messages) {
      if (message.status === "streaming") continue;
      const urls = [...(message.generatedImages ?? []).map((item) => item.dataUrl), ...(message.browserScreenshots ?? []).map((item) => item.dataUrl), ...(message.generatedFiles ?? []).map((item) => item.downloadUrl)].filter(isTemporaryResourceUrl);
      if (!urls.length) continue;
      const key = `${conversation.id}:${message.id}:${urls.join("|")}`;
      if (attempted.current.has(key)) continue;
      attempted.current.add(key); queue.current.push({ key, conversationId: conversation.id, message });
    }
    if (processing.current || !queue.current.length) return;
    processing.current = true; setBusy(true);
    void (async () => {
      try {
        while (queue.current.length) {
          const item = queue.current.shift()!;
          const result = await cacheGeneratedResources(item.message);
          if (Object.keys(result.patch.sourceUrls).length) updateMessage(item.conversationId, item.message.id, (message) => mergeCachedResources(message, result.patch));
          if (result.failed) setFailed((count) => count + result.failed);
        }
      } finally { processing.current = false; setBusy(false); }
    })();
  }, [workspace.conversations, hydrated, updateMessage, revision]);
  const retry = useCallback(() => { if (processing.current) return; attempted.current.clear(); setFailed(0); setRevision((value) => value + 1); }, []);
  return { failed, busy, retry };
}
